/**
 * @file RFQApprovalDrawer.js
 * @description Unified RFQ Approval Drawer Component
 * @version 3.0.0
 *
 * This component combines the functionality of:
 * - RFQSendForApproval.js (Send for approval workflow)
 * - RFQApproveReject.js (Approve/Reject workflow)
 *
 * Modes:
 * - 'send': Send RFQ for approval (from Draft status)
 * - 'approve': Approve RFQ (from PendingForApproval or Draft bulk)
 * - 'reject': Reject RFQ
 *
 * Key Features:
 * - Mode-based conditional rendering
 * - Optimized performance (useMemo, useCallback, React.memo)
 * - Comprehensive error handling
 * - PropTypes validation
 * - Accessibility support
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Row, Col, Container } from 'reactstrap';
import Drawer from '@material-ui/core/Drawer';
import { loggedInUserId, userDetails } from '../../../helper';
import { EMPTY_GUID, RFQAPPROVALTYPEID, RFQ_LISTING, RFQ_APPROVAL_LISTING } from '../../../config/constants';
import { getAllMasterApprovalDepartment } from '../../masters/actions/Material';
import { getReasonSelectList } from '../../costing/actions/Approval';
import { sendToApprover, approveOrRejectRfqByApprover, getAllRfqApprovalUsersLevelFilterByDepartment } from '../actions/rfq';
import { getUsersRfqLevelAPI } from '../../../actions/auth/AuthActions';
import { SearchableSelectHookForm, TextAreaHookForm, AllApprovalField } from '../../layout/HookFormInputs';
import LoaderCustom from '../../common/LoaderCustom';
import Button from '../../layout/Button';
import WarningMessage from '../../common/WarningMessage';
import Toaster from '../../common/Toaster';
import { costingTypeIdToApprovalTypeIdFunction, getRFQIdByPartType } from '../../common/CommonFunctions';
import { REMARKMAXLENGTH } from '../../../config/masterData';

/**
 * RFQApprovalDrawer Component
 * Unified component for all RFQ approval workflows
 *
 * @component
 */
const RFQApprovalDrawer = ({
    // Common props
    isOpen,
    closeDrawer,
    mode = 'send', // 'send' | 'approve' | 'reject'

    // Context props
    approvalRFQId,
    plantId = null,
    divisionId = null,
    approvalTypeId,
    isFinalApprover = false,

    // For 'send' mode
    rfqData = null,
    selectedQuotationIdList = [],

    // For 'approve'/'reject' mode
    selectedRowData = [],
    approvalDetails = {},
    approvalProcessId = null,
    approvalNumber = null,

    // Routing flags
    isFromAddForm = false,
    isFromApprovalTab = false,
    form = 'addRFQ',
}) => {
    // ==================== HOOKS ====================
    const dispatch = useDispatch();
    const history = useHistory();
    const isSubmittingRef = useRef(false);

    // Extract userId once for performance
    const currentUserId = useMemo(() => loggedInUserId(), []);
    const currentUserDetails = useMemo(() => userDetails(), []);

    // ==================== REDUX STATE ====================
    // Combined selector for better performance - single Redux subscription
    // Unified reason API: all modes use state.approval.reasonsList
    const { reasonsList, initialConfiguration } = useSelector((state) => ({
        reasonsList: state?.approval?.reasonsList || [],
        initialConfiguration: state?.auth?.initialConfiguration || {},
    }));

    // Extract configuration flags
    const {
        IsApprovalLevelFilterByPlant = false,
        IsMultipleUserAllowForApproval = false,
        isMultipleDivisionAllowForApproval = false,
    } = initialConfiguration;

    // ==================== DERIVED VALUES ====================
    // Determine if we need department selection
    // Show for: send mode OR approve mode (but NOT for final approver)
    const showDepartmentField = useMemo(() => {
        if (isFinalApprover || approvalDetails?.IsFinalApprover) {
            return false; // Final approver: hide department
        }
        // Show for send mode OR approve mode (intermediate)
        return mode === 'send' || mode === 'approve';
    }, [mode, isFinalApprover, approvalDetails?.IsFinalApprover]);

    // Determine if department should be disabled/frozen
    // Editable in send mode, frozen in approve mode
    const isDepartmentDisabled = useMemo(() => {
        if (mode === 'send') {
            return false; // Editable in send mode
        }
        if (mode === 'approve' && !isFinalApprover && !approvalDetails?.IsFinalApprover) {
            return true; // Frozen in approve mode (intermediate approver)
        }
        return false;
    }, [mode, isFinalApprover, approvalDetails?.IsFinalApprover]);

    // Determine if we need approver selection
    const showApproverField = useMemo(() => {
        if (isFinalApprover || approvalDetails?.IsFinalApprover) return false;
        return mode === 'send' || mode === 'approve';
    }, [mode, isFinalApprover, approvalDetails?.IsFinalApprover]);

    // Determine field requirements based on mode
    // Both reason and remarks are ALWAYS mandatory
    const isReasonMandatory = true;
    const isRemarksMandatory = true;

    // Drawer title based on mode
    const drawerTitle = useMemo(() => {
        switch (mode) {
            case 'send': return 'Send for Approval';
            case 'approve': return 'Approve RFQ';
            case 'reject': return 'Reject RFQ';
            default: return 'RFQ Approval';
        }
    }, [mode]);

    // ==================== FORM STATE ====================
    const {
        handleSubmit,
        control,
        setValue,
        reset,
        register,
        watch,
        formState: { errors },
    } = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            dept: null,
            approver: null,
            reason: null,
            remarks: '',
        },
    });

    // ==================== LOCAL STATE ====================
    // Department state (for 'send' mode)
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [departmentDropdown, setDepartmentDropdown] = useState([]);
    const [isDisableDept, setIsDisableDept] = useState(false);

    // Approver state
    const [approvalDropDown, setApprovalDropDown] = useState([]);
    const [selectedApprover, setSelectedApprover] = useState('');
    const [approverIdList, setApproverIdList] = useState([]);
    const [selectedApproverLevelId, setSelectedApproverLevelId] = useState({
        levelName: '',
        levelId: '',
    });

    // UI state
    const [isLoading, setIsLoading] = useState(false);
    const [isDisable, setIsDisable] = useState(false);
    const [isCheckingFinalUser, setIsCheckingFinalUser] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [noApprovalExistMessage, setNoApprovalExistMessage] = useState('');
    const [isDisableSubmit, setIsDisableSubmit] = useState(false);

    // Level details for sender information
    const [levelDetails, setLevelDetails] = useState(null);

    // Flags to prevent duplicate API calls
    const [hasReasonsFetched, setHasReasonsFetched] = useState(false);
    const [hasApproversFetched, setHasApproversFetched] = useState(false);

    // ==================== COMPUTED VALUES ====================
    /**
     * Transform reasons list for dropdown
     * Memoized to prevent unnecessary re-computation
     */
    const reasonOptions = useMemo(() => {
        if (!Array.isArray(reasonsList) || reasonsList.length === 0) return [];
        return reasonsList
            .map((reason) => ({
                label: reason?.Text || reason?.Reason || reason?.label || '',
                value: reason?.Value || reason?.ReasonId || reason?.value || '',
            }))
            .filter((opt) => opt?.value && opt.value !== '0');
    }, [reasonsList]);

    // Watch form values for validation
    const selectedReason = watch('reason');

    // Watch remarks for validation
    const selectedRemarks = watch('remarks');

    /**
     * Validate form completeness
     * Submit button enabled based on mode requirements
     * Note: React Hook Form handles field-level validation, this checks overall form state
     */
    const isFormValid = useMemo(() => {
        // If final approver, only remarks are needed (validated by React Hook Form)
        if (isFinalApprover || approvalDetails?.IsFinalApprover) {
            return !isDisableSubmit && selectedRemarks?.trim();
        }

        // Common validation for all modes
        let isValid = !isDisableSubmit;
        
        // Reason is ALWAYS mandatory (validated by React Hook Form rules)
        const hasReason = selectedReason && selectedReason.value;
        isValid = isValid && hasReason;
        
        // Remarks is ALWAYS mandatory (validated by React Hook Form rules)
        const hasRemarks = selectedRemarks && selectedRemarks.trim() !== '';
        isValid = isValid && hasRemarks;
        
        // Mode-specific validation
        if (mode === 'send') {
            // Send mode: need department and approver
            const hasDept = selectedDepartment && selectedDepartment.value;
            const hasApprover = IsMultipleUserAllowForApproval
                ? approverIdList.length > 0
                : selectedApprover !== '';
            isValid = isValid && hasDept && hasApprover;
        } else if (mode === 'approve') {
            // Approve mode (intermediate): need department and approver
            // Department validation (always pre-populated from approvalDetails)
            const hasDept = selectedDepartment && selectedDepartment.value;
            isValid = isValid && hasDept;

            // Approver validation (if not final approver)
            if (showApproverField) {
                const hasApprover = IsMultipleUserAllowForApproval
                    ? approverIdList.length > 0
                    : selectedApprover !== '';
                isValid = isValid && hasApprover;
            }
        }

        return isValid;
    }, [
        mode,
        isFinalApprover,
        approvalDetails?.IsFinalApprover,
        selectedDepartment,
        selectedReason,
        selectedRemarks,
        IsMultipleUserAllowForApproval,
        approverIdList,
        selectedApprover,
        isDisableSubmit,
        showApproverField,
    ]);

    /**
     * Extract partType once for use in multiple places
     */
    const partType = useMemo(() => {
        if (mode === 'send') {
            return rfqData?.PartType;
        }
        return approvalDetails?.PartType || selectedRowData[0]?.PartType;
    }, [mode, rfqData, approvalDetails, selectedRowData]);

    // ==================== HELPER FUNCTIONS ====================
    /**
     * Reset all form and component state
     */
    const resetAllState = useCallback(() => {
        reset();
        setSelectedDepartment(null);
        setDepartmentDropdown([]);
        setIsDisableDept(false);
        setApprovalDropDown([]);
        setSelectedApprover('');
        setApproverIdList([]);
        setShowValidation(false);
        setNoApprovalExistMessage('');
        setIsDisableSubmit(false);
        setLevelDetails(null);
        setHasReasonsFetched(false);
        setHasApproversFetched(false);
    }, [reset]);

    /**
     * Transform approver data list to dropdown format
     * Filters out invalid approvers and maps to dropdown structure
     */
    const transformApproversList = useCallback((dataList) => {
        if (!Array.isArray(dataList) || dataList.length === 0) {
            return [];
        }

        return dataList
            .filter(item => item?.Value && item.Value !== '0' && item.Value !== EMPTY_GUID)
            .map(item => ({
                label: item.Text || '',
                value: item.Value,
                levelId: item.LevelId || '',
                levelName: item.LevelName || '',
            }));
    }, []);

    /**
     * Handle approver API response - common logic for setting approvers
     */
    const handleApproversResponse = useCallback((validApprovers, shouldAutoSelect = false) => {
        if (!Array.isArray(validApprovers) || validApprovers.length === 0) {
            setShowValidation(true);
            setNoApprovalExistMessage('No valid approvers found for this department.');
            setApprovalDropDown([]);
            setApproverIdList([]);
            setIsDisableSubmit(true);
            return false;
        }

        const approverIds = validApprovers.map(approver => approver.value);
        setApprovalDropDown(validApprovers);
        setApproverIdList(approverIds);

        // Auto-select first approver if needed and not multiple approval mode
        if (shouldAutoSelect && !IsMultipleUserAllowForApproval && validApprovers.length > 0) {
            const firstApprover = validApprovers[0];
            setValue('approver', firstApprover);
            setSelectedApprover(firstApprover.value);
            setSelectedApproverLevelId({
                levelName: firstApprover.levelName,
                levelId: firstApprover.levelId,
            });
        }

        setShowValidation(false);
        setIsDisableSubmit(false);
        return true;
    }, [IsMultipleUserAllowForApproval, setValue]);

    // ==================== DATA FETCHING ====================
    /**
     * Fetch RFQ level details for sender information
     */
    const fetchRfqLevelDetails = useCallback(() => {
        const rfqId = getRFQIdByPartType(partType);

        if (!rfqId || !currentUserId) {
            setLevelDetails(null);
            return;
        }

        dispatch(getUsersRfqLevelAPI(
            currentUserId,
            rfqId,
            (res) => {
                if (!res?.data?.Result || !res?.data?.Data?.RFQLevels) {
                    setLevelDetails(null);
                    return;
                }

                const rfqLevels = res.data.Data.RFQLevels;

                if (!Array.isArray(rfqLevels) || rfqLevels.length === 0) {
                    setLevelDetails(null);
                    return;
                }

                let levelDetailsTemp = null;

                // Helper function to filter by RFQId (used in multiple places)
                // Matches original logic: element && Number(element.RFQId) === Number(rfqId)
                // Added null safety: check element.RFQId exists before Number conversion
                const filterByRFQId = (elements) => 
                    elements.filter(element => 
                        element && 
                        element.RFQId != null && 
                        Number(element?.RFQId ?? '') === Number(rfqId)
                    );

                // Helper function to extract level from element
                // Matches original logic: element.Level || element.LevelName || ''
                // Note: We already check element exists before calling this, but using optional chaining for safety
                const extractLevel = (element) => {
                    if (!element) return '';
                    return element.Level || element.LevelName || '';
                };

                if (mode === 'send') {
                    // For send mode: filter by RFQId and ApprovalTypeId
                    // Added null safety: check properties exist before Number conversion
                    let filteredData = rfqLevels.filter(
                        element =>
                            element &&
                            element.RFQId != null &&
                            element.ApprovalTypeId != null &&
                            Number(element.RFQId) === Number(rfqId) &&
                            Number(element.ApprovalTypeId) === Number(approvalTypeId)
                    );

                    // Fallback: try without ApprovalTypeId filter
                    if (filteredData.length === 0) {
                        filteredData = filterByRFQId(rfqLevels);
                    }

                    if (filteredData.length > 0 && filteredData[0]) {
                        const firstItem = filteredData[0];
                        // All properties use optional chaining for null safety
                        levelDetailsTemp = {
                            Level: extractLevel(firstItem),
                            LevelId: firstItem?.LevelId || '',
                            RFQId: firstItem?.RFQId || '',
                            ApprovalTypeId: firstItem?.ApprovalTypeId || '',
                            RFQ: firstItem?.RFQ || '',
                        };
                    }
                } else {
                    // For approve/reject mode: filter by RFQId
                    let filteredData = filterByRFQId(rfqLevels);

                    if (filteredData.length > 0 && filteredData[0]) {
                        const firstItem = filteredData[0];
                        levelDetailsTemp = {
                            LevelId: firstItem?.LevelId || '',
                            Level: extractLevel(firstItem),
                        };
                    } else if (rfqLevels.length > 0 && rfqLevels[0]) {
                        // Fallback to first level if filtering fails
                        const firstItem = rfqLevels[0];
                        levelDetailsTemp = {
                            LevelId: firstItem?.LevelId || '',
                            Level: extractLevel(firstItem),
                        };
                    }
                }

                setLevelDetails(levelDetailsTemp);
            },
            (error) => {
                setLevelDetails(null);
            }
        ));
    }, [dispatch, currentUserId, approvalTypeId, partType, mode]);

    /**
     * Fetch approvers for a department
     */
    const fetchApproversForDepartment = useCallback((departmentId) => {
        if (!departmentId) {
            setShowValidation(true);
            setNoApprovalExistMessage('Department ID is required.');
            setIsDisableSubmit(true);
            return;
        }

        setIsCheckingFinalUser(true);
        setNoApprovalExistMessage('');
        setIsDisableSubmit(false);

        const rfqId = approvalRFQId || getRFQIdByPartType(partType);

        if (!rfqId) {
            setIsCheckingFinalUser(false);
            setShowValidation(true);
            setNoApprovalExistMessage('RFQ ID is missing. Cannot fetch approvers.');
            setIsDisableSubmit(true);
            return;
        }

        const requestObject = {
            LoggedInUserId: currentUserId,
            DepartmentId: departmentId,
            rfqId: rfqId,
            ApprovalTypeId: costingTypeIdToApprovalTypeIdFunction(RFQAPPROVALTYPEID ?? '10') || '10',
            PlantId: IsApprovalLevelFilterByPlant ? plantId : null,
            DivisionId: isMultipleDivisionAllowForApproval ? divisionId || null : null,
        };

        dispatch(getAllRfqApprovalUsersLevelFilterByDepartment(requestObject, (resOrError) => {
            setIsCheckingFinalUser(false);

            // Check if it's an error (error objects don't have data property)
            if (resOrError && !resOrError.data) {
                setShowValidation(true);
                setNoApprovalExistMessage('Failed to load approvers. Please try again.');
                setApprovalDropDown([]);
                setApproverIdList([]);
                setIsDisableSubmit(true);
                return;
            }

            // This is a successful response
            if (!resOrError?.data?.Result || !Array.isArray(resOrError?.data?.DataList)) {
                setShowValidation(true);
                setNoApprovalExistMessage('No approvers found for this department.');
                setApprovalDropDown([]);
                setApproverIdList([]);
                setIsDisableSubmit(true);
                return;
            }

            const validApprovers = transformApproversList(resOrError.data.DataList);

            if (!handleApproversResponse(validApprovers, true)) {
                // Error already handled in handleApproversResponse
                return;
            }
        }));
    }, [
        dispatch,
        currentUserId,
        approvalRFQId,
        plantId,
        divisionId,
        IsApprovalLevelFilterByPlant,
        isMultipleDivisionAllowForApproval,
        partType,
        transformApproversList,
        handleApproversResponse,
    ]);

    /**
     * Fetch initial dropdown data (for 'send' mode)
     * - Departments (filtered by user's departments)
     * - Reasons
     */
    const fetchInitialData = useCallback(() => {
        if (mode !== 'send') return;

        setIsLoading(true);

        // Fetch departments
        dispatch(getAllMasterApprovalDepartment((res) => {
            setIsLoading(false);

            if (!res?.data?.SelectList) {
                setDepartmentDropdown([]);
                return;
            }

            const Data = res.data.SelectList;

            // Get user's department names
            const userDepartments = currentUserDetails?.Department;
            const userDeptNames = userDepartments && userDepartments.map(item => item.DepartmentName);

            // Filter departments that match user's departments
            const filteredDepts = Data && Data.filter(item =>
                item?.Value !== '0' && userDeptNames?.includes(item.Text)
            );

            // Transform to dropdown format
            const departmentArray = filteredDepts?.map(item => ({
                label: item?.Text,
                value: item?.Value
            })) || [];

            setDepartmentDropdown(departmentArray);

            // Auto-select department - always select first index
            if (departmentArray?.length > 0) {
                const selectedDept = {
                    label: departmentArray[0]?.label,
                    value: departmentArray[0]?.value
                };

                setValue('dept', selectedDept);
                setSelectedDepartment(selectedDept);
                // Department is now editable in send mode (removed setIsDisableDept(true))

                // Auto-fetch approvers for the selected department
                fetchApproversForDepartment(selectedDept?.value);
            }
        }, (error) => {
            setIsLoading(false);
            setDepartmentDropdown([]);
            Toaster.error('Failed to load departments. Please try again.');
        }));
    }, [dispatch, mode, currentUserDetails, setValue, fetchApproversForDepartment]);

    // ==================== EFFECTS ====================
    /**
     * Load reasons on mount
     * Unified API: all modes use getReasonSelectList
     * Note: getReasonSelectList callback receives either response or error
     */
    useEffect(() => {
        if (isOpen && !hasReasonsFetched) {
            setHasReasonsFetched(true);
            dispatch(getReasonSelectList((responseOrError) => { }));
        }
    }, [isOpen, hasReasonsFetched, dispatch]);

    /**
     * Fetch initial data on mount
     */
    useEffect(() => {
        if (isOpen) {
            fetchRfqLevelDetails();
            if (mode === 'send') {
                fetchInitialData();
            }
        }
    }, [isOpen, mode, fetchRfqLevelDetails, fetchInitialData]);

    /**
     * Pre-populate department for approve mode
     * Shows current department in disabled/frozen state
     */
    useEffect(() => {
        if (isOpen && mode === 'approve' && approvalDetails?.DepartmentId && approvalDetails?.DepartmentName) {
            const deptOption = {
                label: approvalDetails.DepartmentName,
                value: approvalDetails.DepartmentId
            };
            setValue('dept', deptOption);
            setSelectedDepartment(deptOption);
            // Also populate dropdown with this single option for display
            setDepartmentDropdown([deptOption]);
        }
    }, [isOpen, mode, approvalDetails, setValue]);

    /**
     * Fetch approvers for approve/reject mode
     */
    useEffect(() => {
        // Don't fetch approvers if:
        // 1. Drawer is not open
        // 2. User is final approver
        // 3. Already fetched approvers
        // 4. Department ID is not available
        // 5. Action mode is 'reject'
        // 6. Mode is 'send' (handled separately)
        if (
            !isOpen ||
            mode === 'send' ||
            mode === 'reject' ||
            isFinalApprover ||
            approvalDetails?.IsFinalApprover === true ||
            hasApproversFetched ||
            !approvalDetails?.DepartmentId
        ) {
            return;
        }

        const rfqId = getRFQIdByPartType(partType);

        const payload = {
            LoggedInUserId: currentUserId,
            DepartmentId: approvalDetails.DepartmentId,
            rfqId: rfqId,
            ApprovalTypeId: approvalDetails?.ApprovalTypeId || 2,
            PlantId: approvalDetails?.PlantId || selectedRowData[0]?.PlantId || null,
            DivisionId: approvalDetails?.DivisionId || null
        };

        setHasApproversFetched(true);

        dispatch(getAllRfqApprovalUsersLevelFilterByDepartment(
            payload,
            (responseOrError) => {
                // Check if it's an error (errors don't have data.Result)
                if (!responseOrError?.data || responseOrError instanceof Error) {
                    // Error already handled by apiErrors() in action file
                    setApprovalDropDown([]);
                    return;
                }

                // Handle API response with Result=false
                if (!responseOrError?.data?.Result || !Array.isArray(responseOrError?.data?.DataList)) {
                    setApprovalDropDown([]);
                    return;
                }

                // Success: Use existing helper functions to avoid code duplication
                const validApprovers = transformApproversList(responseOrError?.data?.DataList);
                const shouldAutoSelect = validApprovers?.length === 1;
                handleApproversResponse(validApprovers, shouldAutoSelect);
            }
        ));
    }, [
        isOpen,
        mode,
        isFinalApprover,
        hasApproversFetched,
        approvalDetails,
        selectedRowData,
        partType,
        currentUserId,
        dispatch,
        setValue,
        IsMultipleUserAllowForApproval,
        transformApproversList,
        handleApproversResponse,
    ]);

    /**
     * Reset form and state when drawer closes
     */
    useEffect(() => {
        if (!isOpen) {
            resetAllState();
        }
    }, [isOpen, resetAllState]);

    // ==================== EVENT HANDLERS ====================
    /**
     * Handle drawer close
     */
    const handleDrawerClose = useCallback((type) => {
        if (typeof closeDrawer === 'function') {
            closeDrawer(type);
        }
    }, [closeDrawer]);

    /**
     * Handle keyboard events for accessibility
     */
    const toggleDrawer = useCallback((event, closeType = 'cancel') => {
        if (isDisable) {
            return false;
        }

        if (
            event.type === 'keydown' &&
            (event.key === 'Tab' || event.key === 'Shift')
        ) {
            return;
        }

        // Route to appropriate listing page for approve/reject modes
        if (mode !== 'send' && history) {
            if (isFromAddForm) {
                history.push(RFQ_LISTING);
            } else if (isFromApprovalTab) {
                history.push(RFQ_APPROVAL_LISTING);
            }
        }

        handleDrawerClose(closeType);
    }, [isDisable, mode, isFromAddForm, isFromApprovalTab, handleDrawerClose, history]);

    /**
     * Handle department selection change (send mode only)
     */
    const handleDepartmentChange = useCallback((newValue) => {
        setValue('dept', newValue);
        setSelectedDepartment(newValue);

        // Reset approver-related state
        setApprovalDropDown([]);
        setSelectedApprover('');
        setApproverIdList([]);
        setShowValidation(false);
        setNoApprovalExistMessage('');
        setIsDisableSubmit(false);
        setValue('approver', null);

        // Fetch approvers if department is selected
        if (newValue?.value) {
            fetchApproversForDepartment(newValue.value);
        }
    }, [setValue, fetchApproversForDepartment]);

    /**
     * Handle approver selection change
     */
    const handleApproverChange = useCallback((data) => {
        setValue('approver', data);

        if (IsMultipleUserAllowForApproval) {
            // Multiple selection mode - not fully implemented in UI yet
            return;
        }

        // Single selection mode
        if (data && data.value) {
            setSelectedApprover(data.value);
            
            // Get levelId from data, or fallback to lookup from approvalDropDown
            let levelId = data?.levelId || '';
            let levelName = data?.levelName || '';
            
            // If levelId is missing, look up from dropdown
            if (!levelId && approvalDropDown.length > 0) {
                const approverFromDropdown = approvalDropDown.find(
                    approver => approver.value === data.value
                );
                if (approverFromDropdown) {
                    levelId = approverFromDropdown.levelId || '';
                    levelName = approverFromDropdown.levelName || '';
                }
            }
            
            setSelectedApproverLevelId({
                levelName: levelName,
                levelId: levelId,
            });
        } else {
            // Clear selection
            setSelectedApprover('');
            setSelectedApproverLevelId(null);
        }
    }, [setValue, IsMultipleUserAllowForApproval, approvalDropDown]);

    // ==================== FORM SUBMISSION ====================
    /**
     * Handle Send for Approval submission
     */
    const handleSendForApproval = useCallback((formData) => {
        setIsLoading(true);
        setIsDisableSubmit(true);

        // Get QuotationId with multiple fallbacks
        const QuotationId = rfqData?.QuotationId || rfqData?.quotationId || rfqData?.Id || '';

        // Build quotation ID list (multi-select support)
        const multiQuotationIds = Array.isArray(selectedQuotationIdList)
            ? selectedQuotationIdList.filter(Boolean)
            : [];
        const isMultiSelection = multiQuotationIds.length > 1;

        // Validate at least one quotation id exists
        if (!isMultiSelection && !QuotationId) {
            setIsLoading(false);
            setIsDisableSubmit(false);
            isSubmittingRef.current = false;
            Toaster.error('Quotation ID is missing. Cannot send for approval.');
            return;
        }

        // Validate current user ID
        if (!currentUserId) {
            setIsLoading(false);
            setIsDisableSubmit(false);
            isSubmittingRef.current = false;
            Toaster.error('User ID is missing. Cannot send for approval.');
            return;
        }

        // Calculate sender level with comprehensive fallback logic
        // Priority: levelDetails -> approvalDetails -> selectedApproverLevelId -> EMPTY_GUID
        let calculatedSenderLevelId = levelDetails?.LevelId || '';
        let calculatedSenderLevel = levelDetails?.Level || levelDetails?.LevelName || '';

        // Fallback 1: If levelDetails is null, try to use approvalDetails (if available in send mode)
        if (!calculatedSenderLevelId && approvalDetails?.SenderLevelId) {
            calculatedSenderLevelId = approvalDetails.SenderLevelId;
            calculatedSenderLevel = approvalDetails.SenderLevel || approvalDetails.SenderLevelName || '';
        }

        // Fallback 2: For final approvers, use ApproverLevelId as sender level (they approve at their own level)
        if (!calculatedSenderLevelId && (isFinalApprover || approvalDetails?.IsFinalApprover)) {
            calculatedSenderLevelId = approvalDetails?.ApproverLevelId || selectedApproverLevelId?.levelId || '';
            calculatedSenderLevel = approvalDetails?.ApproverLevel || approvalDetails?.ApproverLevelName || selectedApproverLevelId?.levelName || '';
        }

        // Fallback 3: If still no level, use selected approver's level (as last resort)
        if (!calculatedSenderLevelId && selectedApproverLevelId?.levelId) {
            calculatedSenderLevelId = selectedApproverLevelId.levelId;
            calculatedSenderLevel = selectedApproverLevelId.levelName || '';
        }

        // Final validation: Only block if we truly have no sender level AND user is not final approver
        // For final approvers, we allow submission even if level is missing (will use EMPTY_GUID)
        if (!calculatedSenderLevelId && !(isFinalApprover || approvalDetails?.IsFinalApprover)) {
            setIsLoading(false);
            setIsDisableSubmit(false);
            isSubmittingRef.current = false;
            Toaster.error('Unable to send for approval. Sender level details are missing.');
            return;
        }

        // Derive ApprovalRFQId
        const derivedApprovalRFQId = approvalRFQId || getRFQIdByPartType(rfqData?.PartType);

        // Calculate ApproverLevelId with fallback logic
        // Priority: selectedApproverLevelId -> lookup from approvalDropDown -> EMPTY_GUID
        let calculatedApproverLevelId = selectedApproverLevelId?.levelId || '';
        let calculatedApproverLevelName = selectedApproverLevelId?.levelName || '';

        // Fallback: If levelId is empty, look up the selected approver from dropdown
        if (!calculatedApproverLevelId && selectedApprover) {
            const selectedApproverFromDropdown = approvalDropDown.find(
                approver => approver.value === selectedApprover
            );
            if (selectedApproverFromDropdown?.levelId) {
                calculatedApproverLevelId = selectedApproverFromDropdown.levelId;
                calculatedApproverLevelName = selectedApproverFromDropdown.levelName || '';
            }
        }

        // For multiple approvers mode, get levelId from first approver in list
        if (!calculatedApproverLevelId && IsMultipleUserAllowForApproval && approverIdList.length > 0) {
            const firstApproverFromDropdown = approvalDropDown.find(
                approver => approver.value === approverIdList[0]
            );
            if (firstApproverFromDropdown?.levelId) {
                calculatedApproverLevelId = firstApproverFromDropdown.levelId;
                calculatedApproverLevelName = firstApproverFromDropdown.levelName || '';
            }
        }

        // Validate ApproverLevelId is set (required by backend)
        if (!calculatedApproverLevelId) {
            setIsLoading(false);
            setIsDisableSubmit(false);
            isSubmittingRef.current = false;
            Toaster.error('Approver level is missing. Please select an approver with a valid level.');
            return;
        }

        // Build payload with calculated sender level (never empty - will be EMPTY_GUID if all fallbacks fail)
        const payload = {
            ApprovalRFQId: derivedApprovalRFQId,
            ReasonId: formData.reason?.value ? parseInt(formData.reason.value, 10) : 0,
            Reason: formData.reason?.label || '',
            ApproverLevelId: calculatedApproverLevelId,
            ApproverLevel: calculatedApproverLevelName,
            ApproverDepartmentId: formData.dept?.value || '',
            ApproverIdList: IsMultipleUserAllowForApproval
                ? approverIdList
                : [selectedApprover || ''],
            SenderLevelId: calculatedSenderLevelId || EMPTY_GUID,
            SenderLevel: calculatedSenderLevel || '',
            SenderId: currentUserId,
            SenderRemark: formData.remarks?.trim() || '',
            LoggedInUserId: currentUserId,
            ApprovalTypeId: approvalTypeId ? parseInt(approvalTypeId, 10) : 0,
            QuotationIdList: isMultiSelection ? multiQuotationIds : [QuotationId],
            DivisionId: divisionId ? parseInt(divisionId, 10) : null,
            RFQQuotationRequest: form === 'rfqListing' ? null : (rfqData ? rfqData : {})
        };

        dispatch(sendToApprover(
            payload,
            (response) => {
                setIsLoading(false);
                setIsDisableSubmit(false);
                isSubmittingRef.current = false;

                // Close drawer only on success
                if (response?.data?.Result) {
                    handleDrawerClose('submit');
                }
            }
        ));
    }, [
        currentUserId,
        levelDetails,
        rfqData,
        selectedQuotationIdList,
        approvalRFQId,
        divisionId,
        approvalTypeId,
        selectedApproverLevelId,
        IsMultipleUserAllowForApproval,
        approverIdList,
        selectedApprover,
        approvalDetails,
        isFinalApprover,
        approvalDropDown,
        dispatch,
        handleDrawerClose,
        form,
    ]);

    /**
     * Handle Approve or Reject submission
     * Note: Form validation is handled by React Hook Form and isFormValid check
     */
    const handleApproveOrReject = useCallback((formData) => {
        setIsDisable(true);
        setIsLoading(true);

        const reason = formData.reason;
        const remarks = formData.remarks || '';
        const approver = formData.approver;

        // Build approver ID list and get approver level ID
        let finalApproverIdList = [];
        let approverLevelId = EMPTY_GUID;
        let approverLevelName = '';

        if (isFinalApprover || approvalDetails?.IsFinalApprover) {
            // Final level: use approver's own level (1st or 2nd level) instead of EMPTY_GUID
            finalApproverIdList = [];
            approverLevelId = approvalDetails?.ApproverLevelId || EMPTY_GUID;
            approverLevelName = approvalDetails?.ApproverLevel || approvalDetails?.ApproverLevelName || '';
        } else if (mode === 'reject') {
            // Reject: keep behavior without selecting next approver
            finalApproverIdList = [];
            approverLevelId = EMPTY_GUID;
            approverLevelName = '';
        } else {
            // For non-final approver, use selected approvers
            if (IsMultipleUserAllowForApproval) {
                finalApproverIdList = approverIdList;
                // For multiple approvers, get level from first selected approver in dropdown
                if (approvalDropDown.length > 0 && approverIdList.length > 0) {
                    const firstSelectedApprover = approvalDropDown.find(a => a.value === approverIdList[0]);
                    approverLevelId = firstSelectedApprover?.levelId || approvalDetails?.ApproverLevelId || EMPTY_GUID;
                    approverLevelName = firstSelectedApprover?.levelName || '';
                }
            } else {
                finalApproverIdList = approver?.value ? [approver.value] : [currentUserId];
                // Get level ID from selected approver
                approverLevelId = approver?.levelId || approvalDetails?.ApproverLevelId || EMPTY_GUID;
                approverLevelName = approver?.levelName || '';
            }
        }

        // Build approval objects array
        const approvalObjects = selectedRowData.map((item) => {
            // Check status - normalize to handle different formats
            const itemStatus = item?.Status || item?.DisplayStatus || approvalDetails?.Status || approvalDetails?.DisplayStatus || '';
            const normalizedStatus = itemStatus.replace(/\s+/g, '').toLowerCase();
            const isDraftStatus = normalizedStatus === 'draft';

            // For final approvers, if levelDetails is null, use ApproverLevelId as sender level
            // because final approvers approve at their own level
            let calculatedSenderLevelId = levelDetails?.LevelId || approvalDetails?.SenderLevelId || EMPTY_GUID;
            let calculatedSenderLevel = levelDetails?.Level || approvalDetails?.SenderLevel || '';
            
            // Final approver fallback: use ApproverLevelId if levelDetails is null
            if ((isFinalApprover || approvalDetails?.IsFinalApprover) && !levelDetails?.LevelId) {
                calculatedSenderLevelId = approvalDetails?.ApproverLevelId || calculatedSenderLevelId;
                calculatedSenderLevel = approvalDetails?.ApproverLevel || calculatedSenderLevel;
            }
            
            const calculatedSenderId = currentUserId;

            // Base approval object
            const approvalObject = {
                ApprovalProcessSummaryId: item?.ApprovalProcessSummaryId || approvalProcessId || EMPTY_GUID,
                ApprovalToken: item?.ApprovalToken || item?.Token || item?.ApprovalNumber || 0,
                LoggedInUserId: currentUserId,
                SenderLevelId: calculatedSenderLevelId,
                SenderLevel: calculatedSenderLevel,
                SenderId: calculatedSenderId,
                ApproverIdList: finalApproverIdList,
                ApproverLevelId: approverLevelId,
                ApproverLevel: approverLevelName,
                Remark: remarks,
                isSent: true,
                IsApproved: mode === 'approve',
                ApproverDepartmentName: currentUserDetails?.DepartmentName,
                ApproverDepartmentId: item?.DepartmentId || approvalDetails?.DepartmentId || currentUserDetails?.DepartmentId || EMPTY_GUID,
                DivisionId: item?.DivisionId || 0,
                IsFinalApprovalProcess: isFinalApprover || approvalDetails?.IsFinalApprover || false,
                ReasonId: reason?.value || 0,
                IsFromUI: true,
                IsRFQCostingSendForApproval: false,
                IsReturn: false,
            };

            // CRITICAL: Include QuotationId and ApprovalRFQId ONLY for:
            // Draft status AND Final approver (from RFQ Listing)
            // This is the ONLY case where these fields should be sent
            const isFinalApproverFlag = isFinalApprover || approvalDetails?.IsFinalApprover;
            if (form === 'rfqListing' && isDraftStatus && isFinalApproverFlag) {
                approvalObject.QuotationId = item?.QuotationId || approvalDetails?.QuotationId || null;
                approvalObject.ApprovalRFQId = approvalDetails?.ApprovalRFQId || getRFQIdByPartType(item?.PartType || approvalDetails?.PartType) || null;
            }

            // Include VendorList if available
            const vendorList = item?.VendorList || approvalDetails?.VendorList;
            if (vendorList && vendorList.length > 0) {
                approvalObject.VendorList = vendorList;
            }

            return approvalObject;
        });

        // Call API
        dispatch(approveOrRejectRfqByApprover(
            approvalObjects,
            (response) => {
                setIsDisable(false);
                setIsLoading(false);
                isSubmittingRef.current = false;

                // Only navigate and close drawer on success
                if (response?.data?.Result) {
                    // Route to appropriate listing page
                    if (isFromAddForm && history) {
                        history.push(RFQ_LISTING);
                    } else if (isFromApprovalTab && history) {
                        history.push(RFQ_APPROVAL_LISTING);
                    }

                    // Close drawer and trigger refresh
                    handleDrawerClose(mode === 'reject' ? 'reject' : 'submit');
                }
            }
        ));
    }, [
        mode,
        selectedRowData,
        approvalDetails,
        isFinalApprover,
        approvalProcessId,
        currentUserId,
        currentUserDetails,
        levelDetails,
        IsMultipleUserAllowForApproval,
        approverIdList,
        approvalDropDown,
        dispatch,
        isFromAddForm,
        isFromApprovalTab,
        history,
        handleDrawerClose,
        form,
    ]);

    /**
     * Handle form submission - routes to appropriate handler based on mode
     */
    const onSubmit = useCallback(async (formData) => {
        if (isDisable || isLoading || isSubmittingRef.current) {
            return;
        }

        if (!isFormValid) {
            Toaster.warning('Please fill all required fields');
            return;
        }

        isSubmittingRef.current = true;

        if (mode === 'send') {
            handleSendForApproval(formData);
        } else {
            handleApproveOrReject(formData);
        }
    }, [isDisable, isLoading, isFormValid, mode, handleSendForApproval, handleApproveOrReject]);

    // ==================== RENDER ====================
    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={(e) => toggleDrawer(e, 'cancel')}
        >
            <Container>
                <div className={mode === 'send' ? 'drawer-wrapper layout-min-width-680px' : 'drawer-wrapper'}>
                    {isLoading && <LoaderCustom customClass="approve-reject-drawer-loader" />}

                    <form onSubmit={handleSubmit(onSubmit)}>
                        {/* ==================== HEADER ==================== */}
                        <Row className="drawer-heading">
                            <Col>
                                <div className="header-wrapper left">
                                    <h3>{drawerTitle}</h3>
                                </div>
                                <div
                                    onClick={(e) => toggleDrawer(e, 'cancel')}
                                    disabled={isLoading}
                                    className="close-button right"
                                ></div>
                            </Col>
                        </Row>

                        {/* ==================== FORM FIELDS ==================== */}
                        <Row className={mode === 'send' ? '' : 'ml-0'}>
                            {/* ==================== DEPARTMENT ==================== */}
                            {/* Show in send mode (editable) and approve mode (frozen) */}
                            {showDepartmentField && (
                                <div className="input-group form-group col-md-6 input-withouticon">
                                    <SearchableSelectHookForm
                                        label="Department"
                                        name="dept"
                                        Controller={Controller}
                                        control={control}
                                        register={register}
                                        defaultValue=""
                                        options={departmentDropdown}
                                        rules={{ required: true }}
                                        mandatory={true}
                                        placeholder="Select"
                                        disabled={isDepartmentDisabled}
                                        handleChange={handleDepartmentChange}
                                        errors={errors.dept}
                                    />
                                </div>
                            )}

                            {/* ==================== APPROVER ==================== */}
                            {showApproverField && (
                                <div className={`input-group form-group ${mode === 'send' ? 'col-md-6' : mode === 'approve' ? 'col-md-6' : 'col-md-12'} input-withouticon`}>
                                    {isCheckingFinalUser ? (
                                        <div className="text-center py-3 position-relative" style={{ minHeight: '60px' }}>
                                            <LoaderCustom customClass="approver-field-loader" />
                                        </div>
                                    ) : (
                                        <>
                                            {IsMultipleUserAllowForApproval ? (
                                                <AllApprovalField
                                                    label="Approver"
                                                    approverList={approvalDropDown}
                                                    popupButton="View all"
                                                    setApproverIdList={setApproverIdList}
                                                    approverIdList={approverIdList}
                                                />
                                            ) : (
                                                <SearchableSelectHookForm
                                                    label="Approver"
                                                    name="approver"
                                                    Controller={Controller}
                                                    control={control}
                                                    register={register}
                                                    defaultValue=""
                                                    options={approvalDropDown}
                                                    rules={{ required: mode === 'send' || mode === 'approve' }}
                                                    mandatory={mode === 'send' || mode === 'approve'}
                                                    placeholder="Select"
                                                    disabled={
                                                        (mode === 'send' && (!selectedDepartment || approvalDropDown.length === 0 || isCheckingFinalUser)) ||
                                                        (mode === 'approve' && approvalDropDown.length === 0)
                                                    }
                                                    handleChange={handleApproverChange}
                                                    errors={errors.approver}
                                                />
                                            )}
                                        </>
                                    )}

                                    {/* Warning Messages */}
                                    {showValidation && (
                                        <WarningMessage
                                            dClass="mt-2 approver-warning"
                                            message="No approvers found for this department and approval configuration."
                                            textClass=""
                                        />
                                    )}
                                    {noApprovalExistMessage && (
                                        <WarningMessage
                                            dClass="mt-2"
                                            message={noApprovalExistMessage}
                                            textClass=""
                                        />
                                    )}
                                </div>
                            )}

                            {/* ==================== REASON ==================== */}
                            <div className={`input-group form-group ${
                                mode === 'send' ? 'col-md-6' :
                                mode === 'approve' && showApproverField ? 'col-md-6' :
                                'col-md-12'
                            }`}>
                                <SearchableSelectHookForm
                                    label="Reason"
                                    name="reason"
                                    Controller={Controller}
                                    control={control}
                                    register={register}
                                    defaultValue=""
                                    options={reasonOptions}
                                    rules={{ required: isReasonMandatory }}
                                    mandatory={isReasonMandatory}
                                    placeholder="Select"
                                    handleChange={() => {}}
                                    errors={errors.reason}
                                />
                            </div>

                            {/* ==================== REMARKS ==================== */}
                            <div className="input-group form-group col-md-12">
                                <TextAreaHookForm
                                    label="Remark"
                                    name="remarks"
                                    Controller={Controller}
                                    control={control}
                                    register={register}
                                    mandatory={isRemarksMandatory}
                                    rules={{
                                        required: isRemarksMandatory ? 'Remarks are required' : false,
                                        maxLength: {
                                            value: REMARKMAXLENGTH,
                                            message: `Maximum ${REMARKMAXLENGTH} characters allowed`
                                        }
                                    }}
                                    handleChange={() => {}}
                                    placeholder="Type Here..."
                                    className=""
                                    customClassName="withBorder"
                                    rows={4}
                                    errors={errors.remarks}
                                    disabled={false}
                                />
                            </div>
                        </Row>

                        {/* ==================== FOOTER ACTIONS ==================== */}
                        <Row className="sf-btn-footer no-gutters justify-content-between">
                            <div className="col-sm-12 text-right bluefooter-butn">
                                {mode === 'send' ? (
                                    <>
                                        <Button
                                            id="Approval_cancel"
                                            className="mr15 my-0"
                                            variant="cancel-btn"
                                            disabled={false}
                                            onClick={(e) => toggleDrawer(e, 'cancel')}
                                            icon="cancel-icon"
                                            buttonName="Cancel"
                                        />

                                        <button
                                            type="submit"
                                            className="submit-button save-btn"
                                            disabled={isLoading || !isFormValid}
                                        >
                                            <div className="save-icon"></div>
                                            Submit
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            className="reset mr15 cancel-btn"
                                            onClick={(e) => toggleDrawer(e, 'cancel')}
                                            disabled={isDisable}
                                        >
                                            <div className="cancel-icon"></div>
                                            Cancel
                                        </button>

                                        <button
                                            type="submit"
                                            className="submit-button save-btn"
                                            disabled={
                                                isDisable ||
                                                (!(isFinalApprover || approvalDetails?.IsFinalApprover) &&
                                                    mode === 'approve' &&
                                                    approvalDropDown?.length === 0)
                                            }
                                        >
                                            <div className="save-icon"></div>
                                            Submit
                                        </button>
                                    </>
                                )}
                            </div>
                        </Row>
                    </form>
                </div>
            </Container>
        </Drawer>
    );
};

// ==================== PROPTYPES ====================
RFQApprovalDrawer.propTypes = {
    // Common props
    isOpen: PropTypes.bool.isRequired,
    closeDrawer: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['send', 'approve', 'reject']).isRequired,

    // Context props
    approvalRFQId: PropTypes.string,
    plantId: PropTypes.string,
    divisionId: PropTypes.string,
    approvalTypeId: PropTypes.string,
    isFinalApprover: PropTypes.bool,

    // For 'send' mode
    rfqData: PropTypes.object,
    selectedQuotationIdList: PropTypes.arrayOf(PropTypes.string),

    // For 'approve'/'reject' mode
    selectedRowData: PropTypes.array,
    approvalDetails: PropTypes.object,
    approvalProcessId: PropTypes.string,
    approvalNumber: PropTypes.string,

    // Routing flags
    isFromAddForm: PropTypes.bool,
    isFromApprovalTab: PropTypes.bool,
};

RFQApprovalDrawer.defaultProps = {
    mode: 'send',
    plantId: null,
    divisionId: null,
    isFinalApprover: false,
    rfqData: null,
    selectedQuotationIdList: [],
    selectedRowData: [],
    approvalDetails: {},
    approvalProcessId: null,
    approvalNumber: null,
    isFromAddForm: false,
    isFromApprovalTab: false,
};

// Export with React.memo for performance optimization
export default React.memo(RFQApprovalDrawer);
